import {QueryClient, useQuery} from '@tanstack/react-query'
import {useMemo} from 'react'
import {useCognitoAuth} from '../auth'
import {fileQueryKeys, forgetSharePresignedUrls, ShareRoot} from '../widgets/core/storage'
import {getSharingFeatures, listShares, SharesResponse, ShareView} from './api'

/** React Query keys for the sharing API, per signed-in user. */
export const sharingQueryKeys = {
  all: ['sharing'] as const,
  features: (sub: string) => ['sharing', 'features', sub] as const,
  /** Prefix of every user's share list; pass a `sub` for one user's. */
  shares: (sub?: string) =>
    sub === undefined ? (['sharing', 'shares'] as const) : (['sharing', 'shares', sub] as const),
}

/**
 * The flag changes only when someone is added to (or removed from) the pilot, so it is asked once
 * per session in practice. A failure is cached as "no sharing" for as long, rather than asked again
 * by every component that wants to know.
 */
const FEATURES_STALE_MS = 30 * 60 * 1000
const SHARES_STALE_MS = 60 * 1000
/**
 * While a shared folder is being moved by its owner (`moving`), the list is asked again this often,
 * so its members see the folder become writable (and its new name) soon after the move ends.
 */
const SHARES_MOVING_REFRESH_MS = 15 * 1000

/** Whether any of the user's shares is being moved by its owner right now. */
export const anyShareMoving = (data: SharesResponse | undefined): boolean =>
  !!data && (data.sharedByMe.some((s) => s.moving) || data.sharedWithMe.some((s) => s.moving))

/**
 * Whether this user may see sharing (`GET /v1/me/features`). False while unknown, on any error and
 * for everyone the backend has not allowlisted: nothing about sharing is shown unless the server
 * says yes.
 */
export function useSharingAccess(): {sharing: boolean; isLoading: boolean} {
  const {currentUser} = useCognitoAuth()
  const sub = currentUser?.sub ?? ''
  const query = useQuery(sharingQueryKeys.features(sub), getSharingFeatures, {
    enabled: !!sub,
    staleTime: FEATURES_STALE_MS,
    cacheTime: FEATURES_STALE_MS,
    retry: false,
    refetchOnWindowFocus: false,
  })
  return {sharing: query.data?.sharing === true, isLoading: query.isInitialLoading}
}

export interface SharesState {
  /** Folders this user shares (they are the owner). */
  sharedByMe: ShareView[]
  /** Folders others share with this user. */
  sharedWithMe: ShareView[]
  /** Both lists. Empty when sharing is off. */
  shares: ShareView[]
  /** First load, no lists yet. */
  isLoading: boolean
  /** Any request in flight, including a background refresh. */
  isFetching: boolean
  /** The lists came from the server. False while sharing is off, loading, or after a failure. */
  isLoaded: boolean
}

const NO_SHARES: ShareView[] = []

/** The user's shared folders (`GET /v1/shares`). Never requested unless sharing is on. */
export function useShares(): SharesState {
  const {currentUser} = useCognitoAuth()
  const sub = currentUser?.sub ?? ''
  const access = useSharingAccess()
  const enabled = access.sharing && !!sub
  const query = useQuery(sharingQueryKeys.shares(sub), listShares, {
    enabled,
    staleTime: SHARES_STALE_MS,
    retry: false,
    refetchInterval: (data) => (anyShareMoving(data) ? SHARES_MOVING_REFRESH_MS : false),
  })
  // A list cached before sharing was switched off must not keep showing.
  const data = enabled ? query.data : undefined
  const sharedByMe = data?.sharedByMe ?? NO_SHARES
  const sharedWithMe = data?.sharedWithMe ?? NO_SHARES
  const shares = useMemo(() => sharedWithMe.concat(sharedByMe), [sharedByMe, sharedWithMe])
  return {
    sharedByMe,
    sharedWithMe,
    shares,
    isLoading: access.isLoading || (enabled && query.isInitialLoading),
    isFetching: enabled && query.isFetching,
    isLoaded: enabled && query.isSuccess,
  }
}

/**
 * Whether the Files panel has anything to show: the user's own storage (an active subscription,
 * not lapsed), or at least one folder shared with them. Folders the user shares themselves do not
 * count without their own storage. `ownFiles` is false for someone who only has shares from others.
 */
export function useFilesPanelAccess(hasSubscription: boolean): {
  visible: boolean
  ownFiles: boolean
} {
  const {sharedWithMe} = useShares()
  return {visible: hasSubscription || sharedWithMe.length > 0, ownFiles: hasSubscription}
}

/** The root a {@link ShareView} is browsed as. */
export function shareRootFromView(view: ShareView): ShareRoot {
  return {
    kind: 'share',
    id: view.shareId,
    ownerIdentityId: view.ownerIdentityId,
    folderPath: view.folderPath,
    access: view.access,
    name: view.name,
  }
}

/**
 * The server said this share is gone for this user (404) or no longer allows what was asked (403):
 * drop its listings and presigned URLs, and refetch the share list. Nothing else is touched.
 */
export function dropShareCaches(queryClient: QueryClient, shareId: string): void {
  queryClient.removeQueries(fileQueryKeys.share(shareId))
  forgetSharePresignedUrls(shareId)
  void queryClient.invalidateQueries(sharingQueryKeys.shares())
}
